import React from 'react'

import { Container } from 'react-bootstrap'
import styles from './FixStrategies.module.css'

export interface FixStrategiesItem {
    id: string
    title: string
    description: string
}

export interface FixStrategiesData {
    title: string
    description?: React.ReactNode
    strategies?: FixStrategiesItem[]
}

interface FixStrategiesSectionProps {
    data: FixStrategiesData
    titleClass?: string
    descriptionClass?: string
}

const DEFAULT_STRATEGIES: FixStrategiesItem[] = [
    {
        id: 'smarter-targeting',
        title: 'Smarter Targeting',
        description:
            'Reach high-value prospects before they convert, maximizing ROI.',
    },
    {
        id: 'faster-optimization',
        title: 'Faster Optimization',
        description:
            'Adjust campaigns in real time across channels to stay ahead of trends.',
    },
    {
        id: 'personalized-engagement',
        title: 'Personalized Engagement',
        description:
            'Deliver messages that resonate with each customer at right moment.',
    },
    {
        id: 'data-driven-decisions',
        title: 'Data-Driven Decisions',
        description:
            'Convert scattered insights into clear, revenue-generating actions.',
    },
    {
        id: 'reduced-waste',
        title: 'Reduced Waste',
        description:
            'Allocate budget efficiently, focusing on high-intent segments.',
    },
    {
        id: 'predictable-roi',
        title: 'Predictable ROI',
        description:
            'Forecast campaign performance & refine strategy for sustained results.',
    },
]

const FixStrategiesSection: React.FC<FixStrategiesSectionProps> = ({
    data,
    titleClass = '',
    descriptionClass = '',
}) => {
    const { title, description, strategies } = data
    const items =
        strategies && strategies.length > 0 ? strategies : DEFAULT_STRATEGIES

    return (
        <section
            className={[styles.sectionWrapper, styles.sectionBorder]
                .filter(Boolean)
                .join(' ')}
        >
            <Container>
                <div className="">
                    <div className="row align-items-center mb-md-5 mb-3">
                        <div className="col-12">
                            <h2 className={`${styles.sectionTitle} ${titleClass} mb-3`}>
                                {title}
                            </h2>
                            <p
                                className={`${styles.sectionSubTitle} ${descriptionClass} mb-0`}
                            >
                                {description}
                            </p>
                        </div>
                    </div>

                    <div className={`row ${styles.cardsRow}`}>
                        {items.map((item) => (
                            <div key={item.id} className="col-12 col-lg-6">
                                <div className={styles.card}>
                                    <span aria-hidden="true">
                                        <svg
                                            width="25"
                                            height="25"
                                            viewBox="0 0 25 25"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M12.2501 0C19.0144 0 24.5002 5.48573 24.5002 12.2501C24.5002 19.0144 19.0144 24.5001 12.2501 24.5001C5.48573 24.5001 0 19.0144 0 12.2501C0 5.48573 5.48573 0 12.2501 0ZM9.70619 16.229L6.70707 13.2274C6.19613 12.7161 6.19602 11.8824 6.70707 11.3713C7.21823 10.8602 8.05564 10.8634 8.56308 11.3713L10.6774 13.4873L15.9373 8.22749C16.4484 7.71634 17.2822 7.71634 17.7933 8.22749C18.3044 8.73855 18.3037 9.57306 17.7933 10.0835L11.6039 16.2728C11.0935 16.7832 10.259 16.784 9.74794 16.2728C9.73358 16.2585 9.71973 16.2439 9.70619 16.229Z"
                                                fill="#FF952B"
                                            />
                                        </svg>
                                    </span>

                                    <div className={styles.content}>
                                        <h3 className={styles.cardTitle}>{item.title}</h3>
                                        <p className={styles.cardDesc}>{item.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    )
}

export default FixStrategiesSection
